import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { SERVICES, PROJECTS } from "../lib/data";
import Breadcrumb from "../components/Breadcrumb";
import SectionLabel from "../components/SectionLabel";
import ScrollReveal from "../components/ScrollReveal";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = SERVICES.find((s) => s.slug === slug);
  const [openFaq, setOpenFaq] = useState(0);

  if (!service) {
    return (
      <div className="page container-x" style={{ padding: "180px 0" }}>
        <h1>Service not found</h1>
        <button className="btn btn-primary" onClick={() => navigate("/services")} style={{ marginTop: 24 }}>Back to services</button>
      </div>
    );
  }

  const related = PROJECTS.filter((p) =>
    p.tags.some((t) => t.toLowerCase().includes(service.title.toLowerCase().split(" ")[0]))
  );
  const list = related.length ? related : PROJECTS.slice(0, 3);

  return (
    <div className="page" data-testid="service-detail-page">
      <section className="page-hero">
        <div className="container-x">
          <Breadcrumb items={[
            { label: "Home", to: "/" },
            { label: "Services", to: "/services" },
            { label: service.title, to: `/services/${service.slug}` },
          ]} />
          <h1 style={{ marginTop: 16 }}>{service.title}.</h1>
          <p className="lead">{service.short}</p>
          <div className="page-tags">
            <span className="pill pill-dark">Service</span>
            <span className="pill">AI-Powered</span>
            <span className="pill">From {service.starting}</span>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section section-bright">
        <div className="container-x">
          <SectionLabel num={1}>Overview</SectionLabel>
          <h2 className="sec-title">What you get, who it's for, why it matters.</h2>
          <div className="proj-overview-grid">
            <div className="card"><h4>What it is</h4><p>{service.overview.what}</p></div>
            <div className="card"><h4>Who it's for</h4><p>{service.overview.who}</p></div>
          </div>
          <div className="card" style={{ marginTop: 24 }}><h4>The problem it solves</h4><p>{service.overview.problem}</p></div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={2}>What's Included</SectionLabel>
          <h2 className="sec-title">Every {service.title.toLowerCase()} engagement includes.</h2>
          <div className="deliv-grid">
            {service.deliverables.map((d, i) => (
              <ScrollReveal key={d} delay={i * 50}>
                <div className="deliv-card" data-testid={`deliverable-${i}`}>
                  <div className="check"><Check size={16} /></div>
                  <span>{d}</span>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section section-bright">
        <div className="container-x">
          <SectionLabel num={3}>Process</SectionLabel>
          <h2 className="sec-title">How a {service.title.toLowerCase()} project runs.</h2>
          <div className="process-strip">
            {service.process.map((s, i) => (
              <ScrollReveal key={s.t} delay={i * 80}>
                <div className="proc-card">
                  <div className="proc-num">{String(i + 1).padStart(2, "0")}</div>
                  <h4>{s.t}</h4>
                  <p>{s.d}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={4}>Pricing</SectionLabel>
          <h2 className="sec-title">Transparent, scoped pricing.</h2>
          <p className="sec-sub">Custom quotes available — these are starting points.</p>
          <div className="pricing-grid">
            {service.pricing.map((p, i) => (
              <ScrollReveal key={p.tier} delay={i * 100}>
                <div className={`price-card ${p.featured ? "featured" : ""}`} data-testid={`pricing-${p.tier.toLowerCase()}`}>
                  {p.featured && <span className="featured-tag">Most Popular</span>}
                  <div className="tier">{p.tier}</div>
                  <div className="price">{p.price}<small>starting from</small></div>
                  <ul>
                    {p.inc.map((x) => (<li key={x}><Check size={14} /> {x}</li>))}
                  </ul>
                  <div className="delivery">// Delivery {p.delivery}</div>
                  <Link to="/contact" className="btn btn-primary" style={{ marginTop: 8 }} data-testid={`pricing-cta-${p.tier.toLowerCase()}`}>
                    Get Started <ArrowRight size={14} />
                  </Link>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="section section-bright">
        <div className="container-x">
          <SectionLabel num={5}>Related Work</SectionLabel>
          <h2 className="sec-title">Projects that used this service.</h2>
          <div className="work-grid">
            {list.slice(0, 3).map((p, i) => (
              <ScrollReveal key={p.slug} delay={i * 80}>
                <Link to={`/work/${p.slug}`} className="proj-card" data-testid={`related-${p.slug}`}>
                  <div className="proj-img"><img src={p.cover} alt={p.name} loading="lazy" /></div>
                  <div className="proj-body">
                    <div className="proj-meta-row">
                      {p.tags.map((t) => <span key={t} className="pill">{t}</span>)}
                    </div>
                    <div className="proj-byline">{p.client} · {p.year}</div>
                    <h3>{p.name}</h3>
                    <p>{p.short}</p>
                    <span className="arrow-link">View Project <ArrowRight size={14} /></span>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section section-platinum">
        <div className="container-x">
          <SectionLabel num={6}>FAQ</SectionLabel>
          <h2 className="sec-title" style={{ textAlign: "center", marginLeft: "auto", marginRight: "auto" }}>Common questions.</h2>
          <div className="faq-list" data-testid="faq-list">
            {service.faq.map((f, i) => (
              <div key={f.q} className={`faq-item ${openFaq === i ? "open" : ""}`}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-q-${i}`}>
                  <span>{f.q}</span>
                  <ChevronDown className="ic" size={18} />
                </button>
                <div className="faq-a"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="container-x">
        <div className="cta-dark">
          <div className="lead">Ready to start</div>
          <h2>Let's start your {service.title} project.</h2>
          <Link to="/contact" className="btn btn-light" data-testid="cta-banner-contact">Contact Me <ArrowRight size={16} /></Link>
        </div>
      </div>
    </div>
  );
}
